import React from 'react';
import { RefreshCw, AlertTriangle } from 'lucide-react';
import i18n from '../i18n';

/**
 * ErrorBoundary — Global crash catcher for EduGate
 * Catches any unhandled React render errors and shows a friendly recovery page
 * instead of a blank white screen.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Log to console — in production, send to Sentry / logging service
    console.error('[EduGate ErrorBoundary]', error, info.componentStack);
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    const isDev = process.env.NODE_ENV === 'development';
    const isRTL = i18n.language === 'ar';

    return (
      <div
        dir={isRTL ? 'rtl' : 'ltr'}
        className="min-h-screen bg-gradient-to-br from-teal-50 to-teal-100 flex items-center justify-center p-6"
      >
        <div className="bg-white rounded-3xl shadow-2xl p-10 max-w-lg w-full text-center space-y-6">
          {/* Icon */}
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto">
            <AlertTriangle className="w-10 h-10 text-red-500" />
          </div>

          {/* Title */}
          <div>
            <h1 className="text-2xl font-black text-gray-900 mb-2">{i18n.t('eb_title')}</h1>
            <p className="text-gray-500 text-sm leading-relaxed">
              {i18n.t('eb_message')}
            </p>
          </div>

          {/* Dev error details */}
          {isDev && this.state.error && (
            <div className="text-left bg-red-50 border border-red-200 rounded-xl p-4 text-xs text-red-800 font-mono overflow-auto max-h-32">
              {this.state.error.toString()}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => window.location.reload()}
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-teal-800 to-teal-700 text-white font-bold rounded-2xl hover:scale-105 transition-all shadow-lg"
            >
              <RefreshCw className="w-4 h-4" />
              {i18n.t('eb_reload')}
            </button>
            <button
              onClick={() => { window.location.href = '/'; }}
              className="flex items-center gap-2 px-6 py-3 border-2 border-gray-200 text-gray-700 font-bold rounded-2xl hover:bg-gray-50 transition-all"
            >
              {i18n.t('eb_home')}
            </button>
          </div>

          {/* Branding */}
          <p className="text-xs text-muted-foreground font-semibold">EduGate v1.4</p>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
